import { CurbZone, CurbZonePolygonLayerName, EditableCurbZoneLayerName } from "types/curb-zone.type";
import { CurbZoneMapProps, CurbZonePageModeType } from "../curb-zone.type";
import {
  EditableGeoJsonLayer,
  ModifyMode,
  SelectionLayer,
  ViewMode,
  EditAction,
} from "@deck.gl-community/editable-layers";
import { Feature, FeatureCollection, LineString, Polygon, Position } from "geojson";
import { MapType, TooltipType, ViewportPlaceType } from "types";
import { ModeViewType, getPolygonColor, toLineStringFeature } from "constants/map.const";
import { cloneDeep, isEmpty, omit, uniqBy } from "lodash";
import { convertLineToPolygon, convertPolygonToCurbZone, getPlace } from "helper/format-data";
import { copyLineString, filterFeatureIndex } from "helper/map-utils";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { AlterModes } from "../components/editor-mode-list/editor-mode-list.type";
import { CityContext } from "contexts/city-context-provider";
import { CurbZoneMultipleInput } from "../modes/add-curb-zone/curb-zone-prompt.type";
import { GeoJsonLayer } from "@deck.gl/layers";
import { PickingInfo } from "deck.gl";
import { ViewState } from "react-map-gl/mapbox";
import { curbZoneGeoJsonLayerModes } from "@/modules/curb-zone/curb-zone-map.const";
import { editorModeList } from "../components/editor-mode-list/editor-mode-list.const";
import { filterLineStringFromPoint } from "@/helper/map-utils";
import { findObject } from "helper/array";
import { isNil } from "helper/utils";
import { useInteractMap } from "hooks/use-interact-map";
import { usePressKeyboard } from "@/hooks/use-press-keyboard";
import { useQueryCurbZone } from "./use-query-curb-zone";
import useToggle from "hooks/use-toggle";

type UseCurbZoneParams = Pick<CurbZoneMapProps, "appMode"> & {
  isCurbZone: boolean;
};

type UpdateCurbZoneParams =
  | { isType: "create" | "update" | "bulkUpdate"; input: CurbZone[] }
  | { isType: "delete" | "bulkDelete"; input: { id: string }[] };

export const useCurbZone = ({ appMode, isCurbZone }: UseCurbZoneParams) => {
  const { currentCity, viewport, setViewport } = useContext(CityContext);
  const [markerPosition, setMarkerPosition] = useState<ViewportPlaceType | null>(null);
  const [openCurbZoneModal, setOpenCurbZoneModal] = useToggle(false);
  const [openCurbZoneMultipleModal, setOpenCurbZoneMultipleModal] = useToggle(false);
  const [alterMode, setAlterMode] = useState<AlterModes | null>(null);

  const [curbZoneEditionData, setCurbZoneEditionData] = useState<FeatureCollection<LineString, CurbZone>>({
    type: "FeatureCollection",
    features: [],
  });

  const [currentLineParkingList, setCurrentLineParkingList] = useState<Feature<LineString, CurbZone>[]>([]);

  const [modeView, setModeView] = useState<ModeViewType>("normal");

  const [parkingPolygonData, setParkingPolygonData] = useState<Feature<Polygon, CurbZone>[]>([]);
  const [hoverInfo, setHoverInfo] = useState<TooltipType<CurbZone>>();
  const [curbZoneDetail, setCurbZoneDetail] = useState<Feature<Polygon, CurbZone>[]>([]);
  const [mapType, setMapType] = useState<MapType>(MapType.MAPBOX);
  const preParkingEdited = useRef<Feature<LineString, CurbZone>>();
  const preParkingEditedMulti = useRef<Feature<LineString, CurbZone>[]>([]);
  const currentPosition = useRef<Position>([]);
  const preZooming = useRef<ViewState>(null);
  const isCopyMode = useRef(false);

  const { isSpaceBarPressed, isShiftPressed, isAltPressed } = usePressKeyboard({
    appMode,
    multipleMode: [CurbZonePageModeType.MultiEdit],
  });

  const {
    setBounding,
    curbZones,
    loading,
    createCurbZone,
    updateCurbZone,
    removeCurbZone,
    updateMultiCurbZones,
    setIsResetData,
    bulkDeleteCurbZones,
    filter,
    changeFilter,
    selectedDate,
    setSelectedDate,
    totalCalculatedSpaces,
  } = useQueryCurbZone();

  const { onViewStateChange, handleZooming, getCurbZones } = useInteractMap({
    setBounding,
    viewport,
    preZooming,
    filter,
  });

  const currentIndexes = useMemo((): number[] => {
    if (curbZoneDetail.length === 0) {
      return [];
    }
    return filterFeatureIndex(curbZoneEditionData, curbZoneDetail);
  }, [curbZoneEditionData, curbZoneDetail]);

  useEffect(() => {
    const listPolygon = curbZoneEditionData.features.map((item) => ({
      type: "Feature" as const,
      geometry: item.properties.geometry,
      properties: item.properties,
      id: item.id,
    }));

    setParkingPolygonData(listPolygon);
    if (isCopyMode.current) {
      setCurbZoneDetail([listPolygon[listPolygon.length - 1]]);
      const newCurbZoneLineString = curbZoneEditionData.features[curbZoneEditionData.features.length - 1];
      setCurrentLineParkingList([newCurbZoneLineString]);
      preParkingEdited.current = newCurbZoneLineString;
      isCopyMode.current = false;
    }
  }, [curbZoneEditionData]);

  useEffect(() => {
    setAlterMode(null);
    if (appMode !== CurbZonePageModeType.MultiEdit) {
      resetDataOfEditState();
    }
  }, [appMode]);

  useEffect(() => {
    if (isNil(currentCity)) return;
    if (currentCity?.position) {
      currentPosition.current = currentCity?.position;
    }
    setHoverInfo({} as TooltipType<CurbZone>);
    resetDataOfEditState();
  }, [currentCity]);

  useEffect(() => {
    if (!currentCity || loading) {
      return;
    }
    const listParkingEdition: Feature<LineString, CurbZone>[] = [];
    curbZones.forEach((curbZone) => {
      const newParkingEdition = toLineStringFeature(curbZone);
      listParkingEdition.push(newParkingEdition);
    });

    if (currentLineParkingList.length && appMode === CurbZonePageModeType.Edit) {
      const findSelectedParking = findObject(listParkingEdition, "id", currentLineParkingList[0].id);
      if (findSelectedParking > -1) {
        preParkingEdited.current = listParkingEdition[findSelectedParking];
        listParkingEdition[findSelectedParking] = currentLineParkingList[0];
      }
    }
    if (isCopyMode.current && currentLineParkingList.length > 0) {
      listParkingEdition.push(currentLineParkingList[0]);
    }
    setCurbZoneEditionData({
      type: "FeatureCollection",
      features: listParkingEdition,
    });
    setIsResetData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curbZones, loading]);

  useEffect(() => {
    if (!currentCity) {
      return;
    }
    getCurbZones(viewport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, currentCity]);

  const handleAddCurbZoneError = () => {
    /**
     * Remove the new curb-zone line.
     * */
    const newFeatures = curbZoneEditionData.features.toSpliced(-1, 1);
    setCurbZoneEditionData({ type: "FeatureCollection", features: newFeatures });
  };

  const saveCurbZone = async (input: Feature<Polygon, CurbZone>) => {
    if (!currentCity) throw Error("Trying to update a non-existent city");
    const newCurbZone = convertPolygonToCurbZone(input, currentCity.id);

    if (input.id) {
      const res = await updateCurbZone({
        variables: { curbZoneInput: { ...newCurbZone, id: input.id as string } },
      });
      if (res.data?.updateCurbZone) {
        updateCurbZoneEditionData({
          isType: "update",
          input: [{ ...res.data.updateCurbZone, type: "Feature" as const, properties: {} }],
        });
      }
    } else {
      //The backend will create the publishedDate and startDate.
      const newCurbZoneWithoutDate = omit(newCurbZone, ["publishedDate", "startDate"]);
      const res = await createCurbZone({
        variables: { curbZoneInput: newCurbZoneWithoutDate },
      });
      if (res.data?.createCurbZone) {
        updateCurbZoneEditionData({
          isType: "create",
          input: [{ ...res.data.createCurbZone, type: "Feature" as const, properties: {} }],
        });
      } else {
        handleAddCurbZoneError();
      }
    }
  };

  const updateCurbZoneEditionData = ({ input, isType }: UpdateCurbZoneParams) => {
    const newFeatures = [...curbZoneEditionData.features];

    if (isType === "create") {
      const newCurbZoneLineString = toLineStringFeature(input[0]);
      if (isCopyMode.current) {
        newFeatures.push(newCurbZoneLineString);
      } else {
        newFeatures[newFeatures.length - 1] = newCurbZoneLineString;
      }
    } else if (isType === "update" || isType === "bulkUpdate") {
      for (const item of input) {
        const fCurbZone = findObject(newFeatures, "id", item.id);
        if (fCurbZone !== -1) {
          newFeatures[fCurbZone] = toLineStringFeature(item);
        }
      }
    } else {
      for (const item of input) {
        const fCurbZone = findObject(newFeatures, "id", item.id);
        if (fCurbZone !== -1) {
          newFeatures.splice(fCurbZone, 1);
        }
      }
    }

    setCurbZoneEditionData({ type: "FeatureCollection", features: newFeatures });
  };

  /** The function triggered when the editable layer is edited */
  const onEditLayer = async ({ updatedData, editType }: EditAction<FeatureCollection<LineString, CurbZone>>) => {
    if (!isCurbZone) {
      return;
    }

    const clonedData = cloneDeep(updatedData);

    if (appMode === CurbZonePageModeType.MultiEdit) {
      if (["translating", "rotating"].includes(editType)) {
        currentIndexes.forEach((idx) => {
          const newPolygon = convertLineToPolygon(clonedData.features[idx], clonedData.features[idx].properties.width);
          clonedData.features[idx].properties.geometry = newPolygon.geometry;
        });
        setCurbZoneEditionData(clonedData);
      }

      if (["translated", "rotated"].includes(editType)) {
        const newCurrentLineParkingList = [];
        const selectedCurbZones: Feature<LineString, CurbZone>[] = [];

        for (const currentIndex of currentIndexes) {
          if (currentCity?.id && typeof clonedData.features[currentIndex].id === "string") {
            const cloneUpdatedData = cloneDeep(clonedData.features[currentIndex]);
            const geometry = clonedData.features[currentIndex].properties.geometry;
            const newLineParking = convertPolygonToCurbZone(
              { ...clonedData.features[currentIndex], geometry },
              currentCity.id,
            );
            newCurrentLineParkingList.push({ ...newLineParking, id: clonedData.features[currentIndex].id });
            selectedCurbZones.push(cloneUpdatedData);
          }
        }

        await updateMultiCurbZones({
          variables: {
            bulkUpdateCurbZoneInputs: newCurrentLineParkingList,
          },
        });
        setCurrentLineParkingList(selectedCurbZones);
      }
      return;
    }

    let parkingIndex = currentIndexes[0];
    if (appMode === CurbZonePageModeType.Edit) {
      /** Re-calculate the polygon when changing the shape of curb-zone */
      const newPolygon = convertLineToPolygon(
        clonedData.features[parkingIndex],
        clonedData.features[parkingIndex].properties.width,
      );

      clonedData.features[parkingIndex].properties.geometry = newPolygon.geometry;
      setCurbZoneEditionData(clonedData);
    } else {
      setCurbZoneEditionData(clonedData);
    }

    if (["finishMovePosition", "addFeature", "translated", "rotated"].includes(editType)) {
      if (appMode === CurbZonePageModeType.Add) {
        parkingIndex = clonedData.features.length - 1;
      }
      if (!clonedData.features[parkingIndex].properties) {
        return;
      }

      //if user add a new curb-zone or change the length of curb-zone, calculate the number of slot
      const reCalculatedSpaces = ["finishMovePosition", "addFeature"].includes(editType);
      const newLineParking = await getPlace(clonedData.features[parkingIndex], {
        getCalculatedSpaces: reCalculatedSpaces,
      });
      setCurrentLineParkingList([newLineParking]);
      if (appMode === CurbZonePageModeType.Add) {
        preParkingEdited.current = Object.assign({});
        setOpenCurbZoneModal();
      } else {
        await addPropertiesAndSave(newLineParking.properties, newLineParking);
      }
    }
  };

  const editableLayerMode = useMemo(() => {
    if ([CurbZonePageModeType.MultiEdit, CurbZonePageModeType.Edit].includes(appMode)) {
      if (!currentIndexes.length || !alterMode) {
        return ModifyMode;
      }
      /**
       * Alter modes: move or rotate multiple curb-zone
       * **/
      return editorModeList[appMode]?.find((item) => item.label === alterMode)?.mode;
    }
    if (CurbZonePageModeType.Add === appMode) {
      return curbZoneGeoJsonLayerModes[appMode];
    }
    return ViewMode;
  }, [appMode, alterMode, currentIndexes]);

  const editableLayer = new EditableGeoJsonLayer({
    id: EditableCurbZoneLayerName,
    data: { type: curbZoneEditionData.type, features: curbZoneEditionData.features },
    selectedFeatureIndexes: currentIndexes,
    mode: editableLayerMode,
    onEdit: (updateData) => onEditLayer(updateData as EditAction<FeatureCollection<LineString, CurbZone>>),
    updateTriggers: {
      mode: [editableLayerMode, alterMode],
    },
  });

  const polygonLayer = new GeoJsonLayer({
    id: CurbZonePolygonLayerName,
    data: parkingPolygonData,
    stroked: true,
    getFillColor: (feature) => getPolygonColor(feature, "fillColor"),
    getLineColor: (feature) => {
      if (currentIndexes.length && appMode === CurbZonePageModeType.MultiEdit) {
        for (const currentIndex of currentIndexes) {
          if (parkingPolygonData[currentIndex]?.id === feature?.id) {
            return [205, 204, 0, 255];
          }
        }
      }
      return getPolygonColor(feature, "lineColor");
    },
    lineWidthMaxPixels: 1,
    pickable: [CurbZonePageModeType.View, CurbZonePageModeType.Edit].includes(appMode),
    updateTriggers: {
      getLineColor: [appMode, currentIndexes],
      getFillColor: [curbZoneEditionData],
    },
  });

  const handleCurbZoneHover = useCallback(
    (info: PickingInfo) => {
      if (info.layer?.id === CurbZonePolygonLayerName && info.object) {
        setHoverInfo({
          x: info.x,
          y: info.y,
          properties: info.object.properties,
        });
      } else if (!isEmpty(hoverInfo)) {
        setHoverInfo({} as TooltipType<CurbZone>);
      }
    },
    [hoverInfo],
  );

  const currentCityLayer = new GeoJsonLayer({
    id: "current-city",
    data: currentCity?.boundaries,
    filled: false,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 4,
  });

  const selectMultipleCurbZone = ({ pickingInfos }: { pickingInfos: PickingInfo[] }) => {
    if (pickingInfos.length > 0) {
      let selectedCurbZones = filterLineStringFromPoint(pickingInfos);
      if (appMode === CurbZonePageModeType.MultiEdit && isAltPressed) {
        selectedCurbZones = uniqBy([...curbZoneDetail, ...selectedCurbZones], "id");
      }
      setCurbZoneDetail(selectedCurbZones);
      preParkingEditedMulti.current = selectedCurbZones;
      if (preParkingEditedMulti.current) {
        setCurrentLineParkingList(preParkingEditedMulti.current);
      }
    }
  };

  const selectionType = useMemo(() => {
    if (isShiftPressed || appMode !== CurbZonePageModeType.MultiEdit) {
      return "none";
    }
    return isAltPressed ? "rectangle" : "polygon";
  }, [isShiftPressed, appMode, isAltPressed]);

  const selectionVisible = useMemo(() => {
    return appMode === CurbZonePageModeType.MultiEdit && !isSpaceBarPressed && !isShiftPressed;
  }, [appMode, isShiftPressed, isSpaceBarPressed]);

  const selectionMultipleCurbZoneLayer = new SelectionLayer({
    id: "selection-rectangle",
    selectionType,
    layerIds: [EditableCurbZoneLayerName],
    onSelect: selectMultipleCurbZone,
    lineWidthMinPixels: 1,
    visible: selectionVisible,
    updateTriggers: {
      visible: [selectionVisible],
      selectionType: [selectionType],
    },
  });

  /** Add parking area property to parkingAreaProperties */
  const addPropertiesAndSave = async (promptedProperties: CurbZone, lineParking?: Feature<LineString, CurbZone>) => {
    if (openCurbZoneModal) {
      setOpenCurbZoneModal();
    }
    if (currentLineParkingList?.length === 1) {
      const clonedLineParking = cloneDeep(lineParking || currentLineParkingList[0]) as Feature<LineString, CurbZone>;
      const parking = convertLineToPolygon(clonedLineParking, promptedProperties.width);
      parking.properties = {
        ...parking.properties,
        ...promptedProperties,
        editLine: parking.properties?.editLine,
        geoPoint2d: parking.properties?.geoPoint2d,
        geometry: parking.properties?.geometry,
      };
      await saveCurbZone(parking);
      if (openCurbZoneModal) {
        resetDataOfEditState();
      } else if (!isCopyMode.current) {
        /**
         * Keep the Curb-zone selection when not in copy mode.
         * The Curb-zone value will be updated from the backend response.
         */
        setCurbZoneDetail([parking]);
        setCurrentLineParkingList([lineParking || currentLineParkingList[0]]);
        preParkingEdited.current = lineParking || currentLineParkingList[0];
      }
    }
  };

  /** Add new regulations, width and parking angle to multiple Parkings */
  const handleEditMultipleCurbZone = async (input: CurbZoneMultipleInput) => {
    setOpenCurbZoneMultipleModal();
    if (!currentCity) throw Error("Trying to update a non-existent city");
    if (currentLineParkingList) {
      const newCurrentLineParkingList = currentLineParkingList
        .filter((item): item is Feature<LineString, CurbZone> & { id: string } => !!item.id)
        .map((line) => {
          const clonedPrevious = cloneDeep(line);
          const curbZone = convertLineToPolygon(clonedPrevious, input.width);
          const curbPolicyIds = input.curbPolicyIds.length ? input.curbPolicyIds : curbZone.properties.curbPolicyIds;
          const decreeIds = input?.decreeIds?.length ? input.decreeIds : curbZone.properties.decreeIds;
          const streetName = input.streetName?.length ? input.streetName : curbZone.properties.streetName;
          curbZone.properties = {
            ...curbZone.properties,
            streetName,
            curbPolicyIds,
            width: input.width,
            parkingAngle: input.parkingAngle,
            decreeIds,
          };
          const newCurbZone = convertPolygonToCurbZone(curbZone, currentCity.id);
          return {
            ...newCurbZone,
            id: line.id,
          };
        });
      const res = await updateMultiCurbZones({ variables: { bulkUpdateCurbZoneInputs: newCurrentLineParkingList } });
      if (res.data?.bulkUpdateCurbZones) {
        const newCurbZones = res.data.bulkUpdateCurbZones.map((item) => ({
          ...item,
          type: "Feature" as const,
          properties: {},
        }));
        updateCurbZoneEditionData({ isType: "bulkUpdate", input: newCurbZones });
      }
      resetDataOfEditState();
    }
  };

  /**
   * Remove features which indexes are present in `selectedFeatureIndexes`.
   * This may also allow to delete last added features.
   */
  const removeSelectedFeatures = () => {
    const newParkingEdition = [...curbZoneEditionData.features];
    const isEdit = curbZoneDetail[0]?.id;
    if (isEdit) {
      //Modal canceled when edit mode, revert the polygon to what it was before
      const clonedPrevious = cloneDeep(preParkingEdited.current) as Feature<LineString, CurbZone>;
      const newPolygon = convertLineToPolygon(clonedPrevious, clonedPrevious.properties.width);
      newParkingEdition[currentIndexes[0]] = {
        ...clonedPrevious,
        properties: {
          ...clonedPrevious.properties,
          geometry: newPolygon.geometry,
        },
      };
    } else {
      // Modal canceled when in add mode, remove last added parking
      newParkingEdition.pop();
    }
    resetDataOfEditState();
    setCurbZoneEditionData({ type: "FeatureCollection", features: newParkingEdition });
    preParkingEdited.current = undefined;
    if (openCurbZoneModal) {
      setOpenCurbZoneModal();
    }
  };

  const resetDataOfEditState = () => {
    setCurbZoneDetail([]);
    preParkingEdited.current = undefined;
    setCurrentLineParkingList([]);
    isCopyMode.current = false;
  };

  const cancelEditMultiple = () => {
    if (openCurbZoneMultipleModal) setOpenCurbZoneMultipleModal();
    setCurbZoneDetail([]);
    preParkingEditedMulti.current = [] as Feature<LineString, CurbZone>[];
    setCurrentLineParkingList([] as Feature<LineString, CurbZone>[]);
  };

  const showCurbZoneDetail = useMemo(() => {
    if (appMode !== CurbZonePageModeType.Edit) {
      return false;
    }
    return curbZoneDetail && curbZoneDetail.length === 1;
  }, [appMode, curbZoneDetail]);

  const showCurbZoneMultiDetail = useMemo(
    () => appMode === CurbZonePageModeType.MultiEdit && curbZoneDetail && curbZoneDetail.length > 0,
    [appMode, curbZoneDetail],
  );

  const handleSelectCurbZone = (info: PickingInfo) => {
    if ([CurbZonePageModeType.Add, CurbZonePageModeType.View].includes(appMode)) {
      return;
    }

    if (
      !info.object ||
      !info.object?.id ||
      ![EditableCurbZoneLayerName, CurbZonePolygonLayerName].includes(info.layer?.id ?? "")
    ) {
      if (appMode === CurbZonePageModeType.MultiEdit) {
        return;
      }
      resetDataOfEditState();
      cancelEditMultiple();
      return;
    }
    const fParking = findObject(parkingPolygonData, "id", info.object.id);
    if (appMode === CurbZonePageModeType.MultiEdit && isShiftPressed && fParking > -1) {
      // If already selected we do not add it twice
      const indexOfCurrentSelection = findObject(preParkingEditedMulti.current, "id", info.object.id);
      const indexOfFirstSelection = findObject(preParkingEditedMulti.current, "id", preParkingEdited.current?.id);
      if (indexOfCurrentSelection < 0) {
        setCurbZoneDetail((state) => [...state, info.object]);
        // If we have already one parking selected we add it

        if (preParkingEdited.current && preParkingEdited.current?.id && indexOfFirstSelection < 0) {
          preParkingEditedMulti.current.push(preParkingEdited.current);
        }
        preParkingEditedMulti.current.push(curbZoneEditionData.features[fParking]);
        setCurrentLineParkingList(preParkingEditedMulti.current);
      } else {
        setCurbZoneDetail((state) => state.filter((item) => item.id !== info.object.id));
        preParkingEditedMulti.current = preParkingEditedMulti.current.filter((item) => item.id !== info.object.id);
        setCurrentLineParkingList(preParkingEditedMulti.current);
      }
    } else {
      setCurbZoneDetail([parkingPolygonData[fParking]]);
      preParkingEdited.current = curbZoneEditionData.features[fParking];
      setCurrentLineParkingList([curbZoneEditionData.features[fParking]]);
    }
  };

  const cancelChange = () => {
    removeSelectedFeatures();
  };
  const openParkingModal = async () => {
    if (!preParkingEdited.current) {
      return;
    }
    const newLineParking = await getPlace(preParkingEdited.current);
    setCurrentLineParkingList([newLineParking]);
    setOpenCurbZoneModal();
  };

  const curbZoneLayers = [editableLayer, selectionMultipleCurbZoneLayer, polygonLayer, currentCityLayer];

  const selectAddress = (viewport: ViewportPlaceType | null) => {
    setMarkerPosition(viewport);
    if (viewport) {
      setViewport((prevState) => ({ ...prevState, ...viewport, zoom: 16 }));
    }
  };

  const handleDragEnd = (event: PickingInfo) => {
    if (appMode === CurbZonePageModeType.MultiEdit && !isSpaceBarPressed) {
      return;
    }
    if (!event.viewport) {
      return;
    }
    const viewport = event.viewport as unknown as ViewState;
    const newPosition = [viewport.longitude, viewport.latitude];
    currentPosition.current = [...newPosition];
    getCurbZones(viewport);
  };

  const handleDeleteCurbZone = async (curbZoneId: string) => {
    const res = await removeCurbZone({ variables: { curbZoneId } });
    if (res.data?.deleteCurbZone) {
      updateCurbZoneEditionData({ isType: "delete", input: [res.data.deleteCurbZone] });
    }
    resetDataOfEditState();
  };

  const handleBulkDeleteCurbZones = async (input: string[]) => {
    const res = await bulkDeleteCurbZones({ variables: { curbZoneIds: input } });
    if (res.data?.bulkDeleteCurbZones) {
      updateCurbZoneEditionData({ isType: "bulkDelete", input: res.data.bulkDeleteCurbZones });
    }
  };

  const handleCopyCurbZone = async () => {
    const copiedCurbZone = (await copyLineString(currentLineParkingList[0])) as Feature<LineString, CurbZone>;
    isCopyMode.current = true;

    copiedCurbZone.properties.curbPolicyIds = copiedCurbZone.properties.curbPolicies.map(
      (value) => value?.curbPolicyId as string,
    );

    await addPropertiesAndSave(copiedCurbZone.properties, copiedCurbZone);
  };

  return {
    markerPosition,
    setMarkerPosition,
    viewport,
    modeView,
    setModeView,
    selectAddress,
    mapType,
    setMapType,
    curbZoneLayers,
    handleDragEnd,
    handleSelectCurbZone,
    handleZooming,
    hoverInfo,
    handleCurbZoneHover,
    onViewStateChange,
    openCurbZoneModal,
    addPropertiesAndSave,
    currentLineParkingList,
    curbZoneDetail,
    removeSelectedFeatures,
    currentCity,
    showCurbZoneDetail,
    parkingPolygonData,
    currentIndexes,
    resetDataOfEditState,
    openParkingModal,
    handleDeleteCurbZone,
    showCurbZoneMultiDetail,
    cancelEditMultiple,
    setOpenCurbZoneMultipleModal,
    openCurbZoneMultipleModal,
    handleEditMultipleCurbZone,
    cancelChange,
    preZooming,
    handleBulkDeleteCurbZones,
    isSpaceBarPressed,
    filter,
    changeFilter,
    handleCopyCurbZone,
    isCopyMode,
    editableLayer,
    alterMode,
    setAlterMode,
    selectedDate,
    setSelectedDate,
    totalCalculatedSpaces,
  };
};
